<template>
    <div class="benefits-menu">
        <button
            type="button"
            class="benefits-menu-trigger"
            :aria-expanded="open ? 'true' : 'false'"
            aria-haspopup="true"
            @click="open = !open"
        >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#1f2a37" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
        </button>

        <transition name="benefits-menu-fade">
            <div class="benefits-menu-dropdown" v-if="open">
                <button
                    v-for="menuItem in availableItems"
                    :key="menuItem.action"
                    type="button"
                    class="benefits-menu-item"
                    :class="{ 'is-disabled': !menuItem.enabled }"
                    :disabled="!menuItem.enabled"
                    @click="select(menuItem)"
                >
                    <span class="benefits-menu-icon" v-html="menuItem.icon"></span>
                    <span class="benefits-menu-label">{{ t(menuItem.label) }}</span>
                    <span class="benefits-menu-hint" v-if="!menuItem.enabled" v-lang="'dashboard-comingSoon'"></span>
                </button>
            </div>
        </transition>
    </div>
</template>

<script>
const ICONS = {
    statement:
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="8" y1="13" x2="16" y2="13"></line><line x1="8" y1="17" x2="13" y2="17"></line></svg>',
    faq:
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>',
    terms:
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><circle cx="11.5" cy="14.5" r="2.5"></circle><line x1="13.5" y1="16.5" x2="15" y2="18"></line></svg>',
    logout:
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>',
};

const STATEMENT_ENABLED = false;

export default {
    name: "BenefitsMenu",
    props: {
        isLogged: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            open: false,
        };
    },
    computed: {
        availableItems() {
            return [
                STATEMENT_ENABLED
                    ? { action: "statement", label: "dashboard-menuStatement", icon: ICONS.statement, enabled: true, loggedOnly: true }
                    : null,
                { action: "faq", label: "dashboard-menuFaq", icon: ICONS.faq, enabled: true, loggedOnly: false },
                { action: "terms", label: "dashboard-menuTerms", icon: ICONS.terms, enabled: true, loggedOnly: false },
                { action: "logout", label: "dashboard-menuLogout", icon: ICONS.logout, enabled: true, loggedOnly: true },
            ].filter((menuItem) => menuItem && (!menuItem.loggedOnly || this.isLogged));
        },
    },
    mounted() {
        document.addEventListener("click", this.closeOnOutsideClick);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closeOnOutsideClick);
    },
    methods: {
        select(item) {
            if (!item.enabled) {
                return;
            }

            this.open = false;
            this.$emit("select", item.action);
        },
        closeOnOutsideClick(event) {
            if (this.open && !this.$el.contains(event.target)) {
                this.open = false;
            }
        },
    },
};
</script>

<style lang="scss">
.benefits-menu {
  position: relative;
}

.benefits-menu-trigger {
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
}

.benefits-menu-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 20;
  min-width: min(21.9375rem, calc(100vw - 2rem));
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  background: #fcfcfd;
  border-radius: 1rem;
  box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.14);
}

.benefits-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: #384250;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(.is-disabled) {
    background: #f3f4f6;
  }

  &.is-disabled {
    color: #9da4ae;
    cursor: not-allowed;
  }
}

.benefits-menu-icon {
  display: flex;
  flex: 0 0 auto;
}

.benefits-menu-label {
  flex: 1 1 auto;
}

.benefits-menu-hint {
  font-size: 0.75rem;
  color: #9da4ae;
}

.benefits-menu-fade-enter-active,
.benefits-menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.benefits-menu-fade-enter,
.benefits-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}
</style>
